import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, ListTree } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "bloom_level", label: "Bloom Level", placeholder: "e.g. K7 - Synthesize" },
  { key: "question_type", label: "Question Type", placeholder: "e.g. Drag & Drop" },
  { key: "difficulty", label: "Difficulty", placeholder: "e.g. Expert" },
];

export default function Lookups() {
  const [lookups, setLookups] = useState({
    bloom_level: [],
    question_type: [],
    difficulty: [],
  });
  const [loading, setLoading] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lookups");
      setLookups(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = (cat, item) => {
    setConfirmState({
      title: "Remove this option?",
      message: (
        <span>
          You're about to remove <strong>{item.value}</strong> from the {cat.label.toLowerCase()} dropdown.
          Existing questions tagged with this value will keep it; only future choices are affected.
        </span>
      ),
      confirmLabel: "Remove",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/lookups/${item.id}`);
          toast.success("Removed");
          load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  return (
    <PageContainer>
      <PageHeader
        title="Dropdown Options"
        subtitle="Manage the values reviewers can pick for Bloom Level, Question Type, and Difficulty"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {CATEGORIES.map((cat) => (
          <CategoryCard
            key={cat.key}
            cat={cat}
            items={lookups[cat.key] || []}
            loading={loading}
            onAdd={load}
            onDelete={(item) => onDelete(cat, item)}
          />
        ))}
      </div>

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </PageContainer>
  );
}

function CategoryCard({ cat, items, loading, onAdd, onDelete }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const v = val.trim();
    if (!v) return;
    setBusy(true);
    try {
      await api.post(`/lookups/${cat.key}`, { value: v });
      toast.success(`Added "${v}"`);
      setVal("");
      onAdd();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 brand-shadow p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
          <ListTree className="w-4 h-4" />
        </div>
        <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
          {cat.label}
        </h3>
        <span className="ml-auto text-[11px] font-mono text-slate-400">
          {items.length}
        </span>
      </div>

      <form onSubmit={submit} className="flex items-center gap-2 mb-4">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={cat.placeholder}
          data-testid={`lookup-input-${cat.key}`}
          className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        />
        <button
          type="submit"
          disabled={busy || !val.trim()}
          data-testid={`lookup-add-${cat.key}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-semibold"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </form>

      {loading ? (
        <div className="text-center py-6 text-xs text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-xs text-slate-400">No options yet.</div>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`lookup-row-${cat.key}-${it.id}`}
              className="flex items-center justify-between px-3 py-1.5 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-100"
            >
              <span className="text-sm text-slate-800">{it.value}</span>
              <button
                onClick={() => onDelete(it)}
                data-testid={`lookup-delete-${cat.key}-${it.id}`}
                className="p-1 rounded-md text-rose-500 hover:bg-rose-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
